import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

/*
 * Vivid building blocks shared across pages: folder-tab cards (a labelled tab on top of a card in the same fill),
 * round icon buttons, attached tab bars, hero numbers, timelines and day strips. The colours come from the
 * --vivid-* tokens, so they read the same in light and dark mode.
 */

export type FolderTone = 'yellow' | 'lavender' | 'lime' | 'salmon' | 'sky' | 'mint' | 'surface';

export function FolderCard({ tone = 'surface', title, icon, actions, children, className, bodyClassName, as: Tag = 'section' }: { tone?: FolderTone; title: ReactNode; icon?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string; as?: 'section' | 'div' }) {
  return (
    <Tag className={clsx('mp-folder', `mp-folder-${tone}`, className)}>
      <div className="mp-folder-head">
        <h2 className="mp-folder-tab">
          {icon}
          <span className="truncate">{title}</span>
        </h2>
        {actions && <div className="flex shrink-0 items-center gap-1.5 pb-0.5">{actions}</div>}
      </div>
      <div className={clsx('mp-folder-body p-4', bodyClassName)}>{children}</div>
    </Tag>
  );
}

/** A labelled value inside a folder card: icon, small label, bold value. */
export function FolderRow({ icon, label, value, className }: { icon?: ReactNode; label: ReactNode; value: ReactNode; className?: string }) {
  return (
    <div className={clsx('flex items-start gap-3', className)}>
      {icon && <span className="mp-folder-muted mt-1 flex h-5 w-5 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">{icon}</span>}
      <div className="min-w-0">
        <p className="mp-folder-muted text-[0.6875rem] font-medium">{label}</p>
        <div className="truncate text-[0.9375rem] font-semibold leading-snug">{value}</div>
      </div>
    </div>
  );
}

/** Round icon button (36 px; `lg` 40 px) with a hairline, for actions beside tabs and in page headers. */
export function RoundButton({ label, icon, lg, active, className, type = 'button', ...rest }: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & { label: string; icon: ReactNode; lg?: boolean; active?: boolean }) {
  return (
    <button type={type} aria-label={label} data-tip={label} aria-pressed={active} className={clsx('mp-round-btn tip', lg && 'mp-round-btn-lg', active && '!bg-primary !text-primary-fg !border-transparent', className)} {...rest}>
      {icon}
    </button>
  );
}

/** Tab bar whose selected tab attaches to the panel below (`mp-tabpanel` on that panel squares its top-left corner). */
export function TabBar<T extends string>({ value, onChange, tabs, label, className }: { value: T; onChange: (v: T) => void; tabs: { id: T; label: ReactNode; icon?: ReactNode; count?: number }[]; label: string; className?: string }) {
  return (
    <div role="tablist" aria-label={label} className={clsx('mp-tabbar', className)}>
      {tabs.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={value === t.id} onClick={() => onChange(t.id)} className="mp-tab">
          {t.icon}
          {t.label}
          {t.count !== undefined && t.count > 0 && <span className="mp-tab-count">{t.count > 99 ? '99+' : t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** A big thin number with a label (and optional unit / caption). */
export function HeroStat({ value, unit, label, caption, className, size = 'md' }: { value: ReactNode; unit?: ReactNode; label: ReactNode; caption?: ReactNode; className?: string; size?: 'md' | 'lg' }) {
  return (
    <div className={clsx('min-w-0', className)}>
      <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">{label}</p>
      <p className={clsx('mp-hero mt-1', size === 'lg' && 'mp-hero-lg')}>
        {value}
        {unit && <small className="ml-1">{unit}</small>}
      </p>
      {caption && <p className="mt-1.5 text-xs text-muted">{caption}</p>}
    </div>
  );
}

export type DayBlockTone = 'lime' | 'yellow' | 'lavender' | 'salmon' | 'sky' | 'mint' | 'quiet';
const DAY_FILL: Record<DayBlockTone, string> = {
  lime: 'rgb(var(--vivid-lime))',
  yellow: 'rgb(var(--vivid-yellow))',
  lavender: 'rgb(var(--vivid-lavender))',
  salmon: 'rgb(var(--vivid-salmon))',
  sky: 'rgb(var(--vivid-sky))',
  mint: 'rgb(var(--vivid-mint))',
  quiet: 'rgb(var(--ink) / 0.05)',
};

/** A row of blocks, one per day, coloured by what happened (like a schedule strip). */
export function DayStrip({ days, label }: { days: { key: string; tone: DayBlockTone; title: string; weight?: number }[]; label: string }) {
  return (
    <div className="mp-daystrip" role="img" aria-label={label}>
      {days.map((d) => (
        <span key={d.key} title={d.title} style={{ background: DAY_FILL[d.tone], flexGrow: d.weight ?? 1, opacity: d.tone === 'quiet' ? 1 : 0.95 }} />
      ))}
    </div>
  );
}

/** One row of a vertical timeline: a round node with an icon and a date beneath, then the content. */
export function TimelineItem({ icon, tone = 'sky', date, children, className }: { icon: ReactNode; tone?: Exclude<DayBlockTone, 'quiet'> | 'surface'; date: ReactNode; children: ReactNode; className?: string }) {
  const fill = tone === 'surface' ? 'rgb(var(--raised))' : DAY_FILL[tone];
  const ink = tone === 'surface' ? 'rgb(var(--muted))' : `rgb(var(--vivid-${tone}-ink))`;
  return (
    <div className={clsx('flex gap-4', className)}>
      <div className="flex w-11 shrink-0 flex-col items-center gap-1.5">
        <span className="mp-tl-node" style={{ background: fill, color: ink }}>
          {icon}
        </span>
        <span className="text-center text-[0.6875rem] font-medium leading-tight text-subtle">{date}</span>
      </div>
      <div className="min-w-0 flex-1 pb-4">{children}</div>
    </div>
  );
}

export function Timeline({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={clsx('mp-timeline', className)} {...rest}>
      {children}
    </div>
  );
}
